import React, { useState } from 'react';
import Button from '../ui/Button';
import { mapCommunicationsError, removeGupshupKeys, saveGupshupKeys } from '../../services/CommunicationsService';
import type { ProviderStatus } from '../../types/Communications';
import { PHONE_PLACEHOLDER } from '../../constants/placeholders';

interface ProviderPanelProps {
  provider: ProviderStatus | null;
  /** Called after details are saved or removed so the screen re-reads the provider status. */
  onChanged: () => void;
}

const EMPTY = { app_id: '', api_key: '', phone_number_id: '', business_number: '', webhook_secret: '' };

const REASON_TEXT: Record<string, string> = {
  incomplete: 'Some of the Gupshup details are missing. Enter all of them.',
  encryption_unavailable: 'The server cannot store the details securely right now. Try again later or contact support.',
};

/**
 * Admin-only, and the only place WhatsApp (Gupshup) is set up. The values live in this form's inputs while typing
 * and are sent once; they are cleared on save and never shown again (only the masked summary the server returns).
 *
 * Two numbers, both the couple's account, never a guest's: the phone number ID is Meta's id for the number (Gupshup
 * puts it on every incoming webhook, so the webhook can tell the message is for this account); the business phone
 * number is the number guests see, which Gupshup's send call needs as the sender.
 */
const ProviderPanel: React.FC<ProviderPanelProps> = ({ provider, onChanged }) => {
  const [values, setValues] = useState(EMPTY);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const configured = provider?.configured === true;
  const showForm = !configured || editing;
  const complete = Object.values(values).every(v => v.trim() !== '');
  const set = (key: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setValues(prev => ({ ...prev, [key]: e.target.value }));

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await saveGupshupKeys({
        app_id: values.app_id.trim(),
        api_key: values.api_key.trim(),
        phone_number_id: values.phone_number_id.trim(),
        business_number: values.business_number.trim(),
        webhook_secret: values.webhook_secret.trim(),
      });
      setValues(EMPTY);
      setEditing(false);
      setSaved(true);
      onChanged();
    } catch (err) {
      setError(mapCommunicationsError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setError('');
    try {
      await removeGupshupKeys();
      setSaved(false);
      onChanged();
    } catch (err) {
      setError(mapCommunicationsError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const webhookPath = provider?.webhookPath ?? '/webhooks/gupshup';

  return (
    <section className="communications-provider-panel" aria-labelledby="communications-provider-title">
      <h2 id="communications-provider-title" className="communications-provider-title">Connect WhatsApp (Gupshup)</h2>

      {configured && (
        <p className="communications-provider-summary">
          Connected{provider?.masked ? ` (${provider.masked})` : ''}. The saved details are hidden; enter new ones to replace them.
        </p>
      )}
      {!configured && provider?.reason && REASON_TEXT[provider.reason] && (
        <p className="communications-hint">{REASON_TEXT[provider.reason]}</p>
      )}
      {saved && configured && <p className="communications-hint" role="status">Details saved.</p>}
      {error && <div className="communications-error" role="alert">{error}</div>}

      {showForm && (
        <div className="communications-provider-form">
          <div className="form-group">
            <label className="form-label" htmlFor="gupshup-app-id">App ID</label>
            <input id="gupshup-app-id" className="form-control" value={values.app_id} onChange={set('app_id')} autoComplete="off" />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="gupshup-api-key">API key</label>
            <input id="gupshup-api-key" type="password" className="form-control" value={values.api_key} onChange={set('api_key')} autoComplete="new-password" />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="gupshup-phone-id">Phone number ID</label>
            <input id="gupshup-phone-id" className="form-control" value={values.phone_number_id} onChange={set('phone_number_id')} autoComplete="off" inputMode="numeric" placeholder="e.g. 1356541080871363" />
            <p className="communications-hint">Meta's ID for the number, not the phone number. In Gupshup it is on the app's account details.</p>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="gupshup-business-number">Business phone number</label>
            <input id="gupshup-business-number" type="tel" className="form-control" value={values.business_number} onChange={set('business_number')} autoComplete="off" placeholder={PHONE_PLACEHOLDER} />
            <p className="communications-hint">The number guests see messages come from, with its country code.</p>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="gupshup-webhook-secret">Webhook secret</label>
            <input id="gupshup-webhook-secret" type="password" className="form-control" value={values.webhook_secret} onChange={set('webhook_secret')} autoComplete="new-password" />
          </div>
          <div className="communications-provider-actions">
            <Button variant="primary" onClick={save} loading={busy} disabled={!complete}>Save details</Button>
            {configured && <Button variant="secondary" onClick={() => { setEditing(false); setValues(EMPTY); }}>Cancel</Button>}
          </div>
        </div>
      )}

      {configured && !editing && (
        <div className="communications-provider-actions">
          <Button variant="secondary" onClick={() => setEditing(true)}>Replace details</Button>
          <Button variant="danger" onClick={disconnect} loading={busy}>Disconnect</Button>
        </div>
      )}

      <p className="communications-hint">
        In Gupshup, set the webhook to <code>{webhookPath}</code> on your Ans API address, and add the same webhook
        secret as a custom header named <code>x-webhook-secret</code>.
      </p>
    </section>
  );
};

export default ProviderPanel;
